import { Button, LoadingOverlay } from "@mantine/core"
import { useDisclosure } from "@mantine/hooks"
import { IconCheck, IconCurrencyDollar, IconX } from "@tabler/icons-react"
import { motion } from "framer-motion"
import { AlertTriangle, Package, PauseCircle, TrendingUp } from "lucide-react"
import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { useLocation, useParams } from "react-router-dom"
import {
  CollectionQuery,
  Filter,
  Order,
} from "../../../models/collection.model"
import {
  EntityConfig,
  entityViewMode,
} from "../../../models/entity-config.model"
import { Payment, PaymentStatus } from "../../../models/payment.model"
import Confirmation from "../../../shared/component/confirmation/action-confirmation"
import EntityList from "../../../shared/component/entity-list/entity-list.component"
import { useCurrentUser } from "../../../shared/hooks/current-user.hook"
import { dateFormat } from "../../../shared/utility/date-format/date-format"
import { onViewFile } from "../../../shared/utility/view-file"
import { formatCurrency } from "../../dashboard/component/ReportCard"
import StatCard from "../../dashboard/component/stat-cart"
import { exportTenants } from "../api/billing.api"
import ExportPaymentModalComponent from "../component/export-payment-modal.component"
import {
  useChangeBillingStatusMutation,
  useLazyCountBillingsQuery,
  useLazyGetBillingsQuery,
  usePayForOwnerBillManuallyMutation,
} from "../store/billing.query"
import FileViewerModal from "../../../shared/component/file-viewer/file-viewer-modal.component"

export default function BillingPage() {
  const params = useParams()
  const location = useLocation()
  const { t } = useTranslation()
  const [receipt, setReceipt] = useState<File | null>(null)
  const [fileInput, setFileInput] = useState<File | null>(null)

  const [fileOpened, { open: fileOpen, close: fileClose }] = useDisclosure()
  const [selectedFile, setSelectedFile] = useState<string[]>([])

  const [viewMode, setViewMode] = useState<entityViewMode>("list")
  const [openedExport, { open: openExport, close: closeExport }] =
    useDisclosure()
  const [openedImport, { open: openImport, close: closeImport }] =
    useDisclosure()

  const [collection, setCollection] = useState<CollectionQuery>({
    skip: 0,
    top: 10,
    includes: [
      "tenant",
      "lease",
      "lease.unit",
      "unit.property",
      // "property.owner",
      "owner",
    ],
    orderBy: [{ field: "createdAt", direction: "desc" }],
  })

  const [getPayments, payments] = useLazyGetBillingsQuery()
  const [countPayments, totalPayments] = useLazyCountBillingsQuery()
  const [countPendingPayments, pendingPayments] = useLazyCountBillingsQuery()
  const [countSuccessPayments, successPayments] = useLazyCountBillingsQuery()
  const [countRejectedPayments, rejectedPayments] = useLazyCountBillingsQuery()
  const [userInfo, userId, ownerInfo] = useCurrentUser()
  const [changeStatus, changeStatusResponse] = useChangeBillingStatusMutation()
  const [payForOwnerBill, payForOwnerBillResponse] =
    usePayForOwnerBillManuallyMutation()

  const generateFilterOption = (status?: PaymentStatus): Filter[][] => {
    if (userInfo.role.key == "super_admin" && status) {
      // return [[{ field: "enabled", value: value as boolean, operator: "=" }]]
      return [
        [{ field: "paymentStatus", operator: "=", value: status }],
        [{ field: "leaseId", operator: "isNull" }],
      ]
    }

    if (status) {
      return [
        // [{ field: "enabled", value: value as boolean, operator: "=" }],
        [{ field: "ownerId", value: ownerInfo?.parentOwnerId, operator: "=" }],
        [{ field: "leaseId", operator: "isNull" }],
        [{ field: "paymentStatus", operator: "=", value: status }],
      ]
    }

    return []
  }
  useEffect(() => {
    if (userInfo?.role?.key == "super_admin") {
      getPayments({
        ...collection,
        filter: [[{ field: "leaseId", operator: "isNull" }]],

        // filter: [
        //   [{ field: "owner.confidential", value: false, operator: "=" }],
        // ],
      })
      console.log(
        "🚀 ~ BillingPage ~ getPayments: heeeeeeeeeeeeeeeeeeeeeeeeeeeeeee",
      )
    } else {
      console.log(
        "🚀 ~ BillingPage ~ getPayments: haaaaaaaaaaaaaaaaaaaaaaaaaaaaaa",
      )
      getPayments({
        ...collection,
        filter: [
          [
            {
              field: "ownerId",
              value: ownerInfo?.parentOwnerId,
              operator: "=",
            },
          ],
          [{ field: "leaseId", operator: "isNull" }],
        ],
      })
    }
  }, [collection])
  useEffect(() => {
    if (userInfo.role.key == "super_admin") {
      countPayments({
        count: true,
        filter: [[{ field: "leaseId", operator: "isNull" }]],
      })
    } else {
      countPayments({
        count: true,
        filter: [
          [
            {
              field: "ownerId",
              value: ownerInfo?.parentOwnerId,
              operator: "=",
            },
          ],
          [{ field: "leaseId", operator: "isNull" }],
        ],
      })
    }
    countPendingPayments({
      count: true,
      filter: generateFilterOption(PaymentStatus.Pending),
    })
    countRejectedPayments({
      count: true,
      filter: generateFilterOption(PaymentStatus.Rejected),
    })
    countSuccessPayments({
      count: true,
      filter: generateFilterOption(PaymentStatus.Success),
    })
  }, [])

  useEffect(() => {
    if (params?.id !== undefined) {
      setViewMode("detail")
    } else {
      setViewMode("list")
    }
    if (location.pathname === "/tenant/archived") {
      setViewMode("detail")
    }
  }, [setViewMode, params?.id, location])

  const data: Payment[] | undefined = payments.data?.data

  const config: EntityConfig<Payment> = {
    primaryColumn: {
      key: "name",
      name: t("lease", { count: 1 }),
      render(value) {
        return value?.lease?.name
      },
    },
    detailUrl: "detail",
    rootUrl: "/tenant",
    identity: "id",
    name: "name",
    showDetail: false,
    visibleColumn: [
      {
        key: "amount",
        name: t("amount"),
        render: (payment) => {
          return `${formatCurrency(payment?.amount as number)} ETB`
        },
      },
      {
        key: "owner",
        name: t("owner", { count: 1 }),
        render(value) {
          return value?.owner?.name
        },
      },
      {
        key: "paymentProof",
        name: t("paymentProof"),
        render(value) {
          return (
            <>
              {value?.minioFile && (
                <span
                  // target="_blank"
                  // rel="noopener noreferrer"
                  // href={tenant?.data?.minoioIdentification}
                  // onClick={() => onViewFile(value?.file)}
                  onClick={() => {
                    setSelectedFile([value?.minioFile as string])
                    fileOpen()
                  }}
                  className=" cursor-pointer  text-white inline-block bg-primary  text-sm px-3 py-1 rounded-full"
                >
                  {t("view")}
                </span>
              )}
            </>
          )
        },
      },
      {
        key: "receipt",
        name: t("receipt"),
        render(value) {
          return (
            <>
              {value?.minioReceipt && (
                <span
                  // target="_blank"
                  // rel="noopener noreferrer"
                  // href={tenant?.data?.minoioIdentification}
                  // onClick={() => onViewFile(value?.receipt)}
                  onClick={() => {
                    setSelectedFile([value?.minioReceipt as string])
                    fileOpen()
                  }}
                  className=" cursor-pointer  text-white inline-block bg-primary  text-sm px-3 py-1 rounded-full"
                >
                  {t("view")}
                </span>
              )}
            </>
          )
        },
      },
      { key: "paymentMethod", name: t("paymentMethod") },
      { key: "paymentStatus", name: t("paymentStatus") },
      {
        key: "paymentDate",
        name: t("paymentDate"),
        render(value) {
          return dateFormat(value?.paymentDate, {
            day: true,
            month: true,
            weekday: true,
            year: true,
          })
        },
      },

      {
        key: "action",
        name: t("action"),
        render(payment) {
          return (
            <>
              {userInfo?.role?.key === "owner" ? (
                <>
                  {payment?.paymentStatus === PaymentStatus.Pending && (
                    <Confirmation
                      type={"notify"}
                      message={
                        "Are you sure you want to perform manual payment on this billing? If so, please upload a payment proof below."
                      }
                      header={`Pay Manually`}
                      yesText={"Pay Manually"}
                      onYes={(reason: string) => {
                        if (fileInput) {
                          const formData = new FormData()
                          formData.set("paymentId", payment?.id as string)
                          formData.set("file", fileInput as File)
                          payForOwnerBill({
                            paymentId: payment?.id as string,
                            data: formData,
                          })
                        }
                      }}
                      acceptsFile={userInfo?.role?.key === "owner"}
                      fileLabel="Upload payment proof"
                      onAcceptingFile={(item: File) => {
                        if (userInfo?.role?.key === "owner") {
                          setFileInput(item)
                        }
                      }}
                    >
                      <Button
                        variant="filled"
                        loading={
                          payForOwnerBillResponse.isLoading &&
                          payForOwnerBillResponse.originalArgs?.paymentId ===
                            payment?.id
                        }
                        className={`bg-green-500 text-white flex space-x-2 items-center`}
                        type="button"
                        classNames={{ label: "flex space-x-1" }}
                        size={"xs"}
                      >
                        <IconCurrencyDollar size={20} strokeWidth={1.5} />
                        {/* <span>Pay Manually</span>{" "} */}
                      </Button>
                    </Confirmation>
                  )}
                </>
              ) : (
                <>
                  {payment?.paymentStatus == PaymentStatus.Pending && (
                    <div className="flex gap-2">
                      <Confirmation
                        type={"notify"}
                        message={t("approvePaymentMessage")}
                        onYes={() => {
                          // deleteRentIncrease(payment?.id as string)
                          const formData = new FormData()

                          formData.append("id", payment?.id as string)
                          formData.append("status", PaymentStatus.Success)
                          receipt && formData.append("receipt", receipt)

                          changeStatus({
                            id: payment?.id as string,
                            data: formData,
                          })
                          close()
                        }}
                        acceptsFile={
                          userInfo?.role?.key == "owner" ||
                          userInfo?.role?.key == "super_admin"
                        }
                        fileLabel={t("receipt")}
                        onAcceptingFile={(file: File) => {
                          setReceipt(file)
                        }}
                        header={t("approvePaymentTitle")}
                      >
                        <Button
                          variant="filled"
                          loading={
                            changeStatusResponse.isLoading &&
                            changeStatusResponse?.originalArgs?.id ===
                              payment?.id
                          }
                          className={`bg-green-500 text-white flex space-x-2 items-center`}
                          type="button"
                          classNames={{ label: "flex space-x-1" }}
                          size={"xs"}
                        >
                          <IconCheck size={20} strokeWidth={1.5} />
                        </Button>
                      </Confirmation>

                      <Confirmation
                        type={"danger"}
                        message={t("paymentRejectionMessage")}
                        onYes={() => {
                          const formData = new FormData()

                          formData.append("id", payment?.id as string)
                          formData.append("status", PaymentStatus.Rejected)
                          // deleteRentIncrease(payment?.id as string)
                          changeStatus({
                            id: payment?.id as string,
                            data: formData,
                          })
                          close()
                        }}
                        header={t("paymentRejectionTitle")}
                      >
                        <Button
                          variant="filled"
                          loading={
                            changeStatusResponse.isLoading &&
                            changeStatusResponse?.originalArgs?.id ===
                              payment?.id
                          }
                          className={`bg-danger text-white flex space-x-2 items-center`}
                          type="button"
                          classNames={{ label: "flex space-x-1" }}
                          size={"xs"}
                        >
                          <IconX size={20} strokeWidth={1.5} />
                        </Button>
                      </Confirmation>
                    </div>
                  )}
                </>
              )}
            </>
          )
        },
      },
    ],
  }

  // const _parseConfig = () => {
  //   if (userInfo?.role?.key === "tenant") {
  //     return {
  //       ...config,
  //       visibleColumn: config.visibleColumn.filter(
  //         (column) => column.key !== "action",
  //       ),
  //     }
  //   } else {
  //     return config
  //   }
  // }

  return (
    <main className="max-w-7xl mx-auto py-6 px-4 lg:px-8 mt-16">
      <LoadingOverlay visible={payments.isFetching} />

      <motion.div
        className="hidden md:grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4 mb-8"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1 }}
      >
        <StatCard
          name={t("totalBillings")}
          icon={Package}
          value={totalPayments?.data?.count}
          color="#6366F1"
        />
        <StatCard
          name={t("pendingBillings")}
          icon={PauseCircle}
          value={pendingPayments?.data?.count}
          color="#8B5CF6"
        />
        <StatCard
          name={t("successBillings")}
          icon={TrendingUp}
          value={successPayments?.data?.count}
          color="#90EE90"
        />
        <StatCard
          name={t("rejectedBillings")}
          icon={AlertTriangle}
          value={rejectedPayments?.data?.count}
          color="#EC4899"
        />

        {/* <StatCard name='Total Revenue' icon={DollarSign} value='12.5%' color='#10B981' /> */}
      </motion.div>
      <EntityList
        viewMode={viewMode}
        title={t("billing", { count: 2 })}
        showExport={true}
        // header={
        //   <>
        //     <div className="h-14 dark:border-gray-500 dark:bg-gray-500 flex items-center justify-between p-2 border-0 border-b">
        //       <div className="h-full flex items-center space-x-4 font-semibold dark:text-white">
        //         {t("payment", { count: 2 })}
        //       </div>
        //       {
        //         <div className="h-full  space-x-4 flex items-center">
        //           {viewMode == "list" && (
        //             <>
        //               <Checkbox
        //                 size="sm"
        //                 onChange={(e) =>
        //                   setCollection({
        //                     ...collection,
        //                     withArchived: e.currentTarget.checked
        //                       ? true
        //                       : false,
        //                   })
        //                 }
        //                 label={
        //                   <span className="text-xs text-gray-500 dark:text-gray-400">
        //                     {t("showArchived")}
        //                   </span>
        //                 }
        //               />

        //               <Button
        //                 classNames={{ label: "flex space-x-2 text-white" }}
        //                 className="bg-primary dark:bg-dark_primary"
        //                 size="xs"
        //                 type="button"
        //                 onClick={(e) => openExport()}
        //                 variant="filled"
        //               >
        //                 <span className="text-white">
        //                   <IconFileExport />
        //                 </span>
        //                 <span>{t("export")}</span>
        //               </Button>
        //               <Button
        //                 classNames={{ label: "flex space-x-2 text-white" }}
        //                 className="bg-primary dark:bg-dark_primary"
        //                 size="xs"
        //                 type="button"
        //                 onClick={(e) => openImport()}
        //                 variant="filled"
        //               >
        //                 <span className="text-white">
        //                   <IconFileImport />
        //                 </span>
        //                 <span>{t("import")}</span>
        //               </Button>
        //             </>
        //           )}
        //           <Link to={`/tenant/new`} state={{ id: location?.state?.id }}>
        //             <Button
        //               classNames={{ label: "flex space-x-2 text-white" }}
        //               className="bg-primary dark:bg-dark_primary"
        //               size="xs"
        //               variant="filled"
        //             >
        //               <span className="text-white">
        //                 <svg
        //                   xmlns="http://www.w3.org/2000/svg"
        //                   className="fill-current h-4 text-white"
        //                   viewBox="0 0 24 24"
        //                 >
        //                   <path
        //                     fillRule="evenodd"
        //                     d="M11 2L11 11L2 11L2 13L11 13L11 22L13 22L13 13L22 13L22 11L13 11L13 2Z"
        //                   />
        //                 </svg>
        //               </span>
        //               <span>{t("new")}</span>
        //             </Button>
        //           </Link>
        //         </div>
        //       }
        //     </div>
        //   </>
        // }
        showNewButton={false}
        total={payments?.data?.count}
        itemsLoading={payments.isFetching}
        config={config}
        items={data}
        initialPage={1}
        defaultPageSize={10}
        pageSize={[10, 15, 20]}
        collectionQuery={collection}
        onArchivedChecked={(e) =>
          setCollection({
            ...collection,
            withArchived: e,
          })
        }
        onPaginationChange={(skip: number, top: number) => {
          if (collection.skip !== skip || collection.top !== top) {
            setCollection({ ...collection, skip: skip, top: top })
          }
        }}
        onSearch={(data: any) => {
          setCollection({
            ...collection,
            search: data,
            searchFrom: ["name"],
          })
        }}
        onFilterChange={(data: any) => {
          if (collection?.filter || data.length > 0) {
            setCollection({ ...collection, filter: data })
          }
        }}
        onOrder={(data: Order) =>
          setCollection({ ...collection, orderBy: [data] })
        }
        onExport={openExport}
      />

      <ExportPaymentModalComponent
        open={openExport}
        opened={openedExport}
        close={closeExport}
        filename={"Payments.xlsx"}
        filteringVariables={[
          [
            {
              field: "paymentStatus",
              value: PaymentStatus.Pending,
              name: PaymentStatus.Pending,
              operator: "=",
            },
            {
              field: "paymentStatus",
              value: PaymentStatus.Success,
              name: PaymentStatus.Success,
              operator: "=",
            },
            {
              field: "paymentStatus",
              value: PaymentStatus.Expired,
              name: PaymentStatus.Expired,
              operator: "=",
            },
            {
              field: "paymentStatus",
              value: PaymentStatus.Failed,
              name: PaymentStatus.Failed,
              operator: "=",
            },
            {
              field: "paymentStatus",
              value: PaymentStatus.Rejected,
              name: PaymentStatus.Rejected,
              operator: "=",
            },
          ],
        ]}
        exportFunction={exportTenants}
      />

      <FileViewerModal
        open={fileOpen}
        close={fileClose}
        files={selectedFile}
        opened={fileOpened}
        fileType={t("payment", { count: 1 })}
        fileNames={[]}
        remove={() => {}}
        removeLoading={false}
      />
    </main>
  )
}
